import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Paper,
  Tabs,
  Tab,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  ContentCopy as ContentCopyIcon,
  QrCode as QrCodeIcon,
  Link as LinkIcon,
  CheckCircle as CheckCircleIcon,
  Share as ShareIcon,
} from '@mui/icons-material';

interface InviteLinksProps {
  organizerId: number;
  eventId?: number;
  organizerName: string;
}

const InviteLinks: React.FC<InviteLinksProps> = ({
  organizerId,
  eventId,
  organizerName,
}) => {
  const [open, setOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [copiedLink, setCopiedLink] = useState<string | null>(null);

  const baseUrl = window.location.origin;
  
  // Ссылки
  const profileLink = `${baseUrl}/organizer/${organizerId}`;
  const eventsLink = eventId
    ? `${baseUrl}/events?organizer_id=${organizerId}`
    : `${baseUrl}/events?organizer_id=${organizerId}`;
  
  // Ссылки с UTM
  const getUtmLink = (source: string, medium: string, campaign: string) => {
    const base = eventId
      ? `${baseUrl}/events?organizer_id=${organizerId}`
      : `${baseUrl}/organizer/${organizerId}`;
    return `${base}&utm_source=${source}&utm_medium=${medium}&utm_campaign=${campaign}`;
  };

  const handleCopy = (text: string, linkName: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedLink(linkName);
      setTimeout(() => setCopiedLink(null), 2000);
    }).catch(err => {
      console.error('Failed to copy:', err);
    });
  };

  const handleDownloadQR = (filename: string) => {
    // Находим SVG элемент QR-кода
    const svgElement = document.getElementById(`qr-${filename}`);
    if (!svgElement) {
      console.error('QR code element not found:', `qr-${filename}`);
      return;
    }

    // Сериализуем SVG в строку
    const svgData = new XMLSerializer().serializeToString(svgElement);
    
    // Создаем Blob из SVG
    const blob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const svgUrl = URL.createObjectURL(blob);
    
    // Создаем временный canvas для конвертации в PNG
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      // Устанавливаем размер canvas равным размеру изображения
      canvas.width = img.width;
      canvas.height = img.height;
      
      // Рисуем изображение на canvas
      if (ctx) {
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        
        // Конвертируем в PNG и скачиваем
        const pngUrl = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.download = `${filename}.png`;
        link.href = pngUrl;
        link.click();
        
        // Очищаем
        URL.revokeObjectURL(svgUrl);
      }
    };
    
    img.src = svgUrl;
  };

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<ShareIcon />}
        onClick={() => setOpen(true)}
        sx={{ mr: 1 }}
      >
        📢 Пригласить
      </Button>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          📢 Пригласительные ссылки от {organizerName}
        </DialogTitle>
        <DialogContent>
          <Tabs
            value={tabValue}
            onChange={(_, newValue) => setTabValue(newValue)}
            sx={{ mb: 3 }}
          >
            <Tab icon={<LinkIcon />} label="Ссылки" iconPosition="start" />
            <Tab icon={<QrCodeIcon />} label="QR-коды" iconPosition="start" />
          </Tabs>

          {tabValue === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Поделитесь этими ссылками с потенциальными участниками. 
                Мы будем отслеживать переходы и регистрации.
              </Alert>

              {/* Основная ссылка */}
              <Box>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  🔗 Ссылка на все мероприятия
                </Typography>
                <TextField
                  fullWidth
                  value={eventsLink}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => handleCopy(eventsLink, 'events')}
                          edge="end"
                        >
                          {copiedLink === 'events' ? <CheckCircleIcon color="success" /> : <ContentCopyIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {/* Ссылка на профиль */}
              <Box>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  👤 Ссылка на профиль организатора
                </Typography>
                <TextField
                  fullWidth
                  value={profileLink}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => handleCopy(profileLink, 'profile')}
                          edge="end"
                        >
                          {copiedLink === 'profile' ? <CheckCircleIcon color="success" /> : <ContentCopyIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {/* UTM ссылки */}
              <Box>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  📊 Ссылки для соцсетей (с UTM-метками)
                </Typography>
                <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Для Instagram/VK
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        value={getUtmLink('social', 'post', 'organic')}
                        InputProps={{
                          readOnly: true,
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => handleCopy(getUtmLink('social', 'post', 'organic'), 'utm-social')}
                                size="small"
                              >
                                {copiedLink === 'utm-social' ? <CheckCircleIcon color="success" fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>

                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Для email-рассылки
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        value={getUtmLink('email', 'newsletter', 'monthly')}
                        InputProps={{
                          readOnly: true,
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => handleCopy(getUtmLink('email', 'newsletter', 'monthly'), 'utm-email')}
                                size="small"
                              >
                                {copiedLink === 'utm-email' ? <CheckCircleIcon color="success" fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>

                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Для мессенджеров (Telegram, WhatsApp)
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        value={getUtmLink('messenger', 'share', 'direct')}
                        InputProps={{
                          readOnly: true,
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => handleCopy(getUtmLink('messenger', 'share', 'direct'), 'utm-messenger')}
                                size="small"
                              >
                                {copiedLink === 'utm-messenger' ? <CheckCircleIcon color="success" fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>
                  </Box>
                </Paper>
              </Box>
            </Box>
          )}

          {tabValue === 1 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center' }}>
              <Alert severity="info" sx={{ mb: 2, width: '100%' }}>
                Скачайте QR-коды и используйте их для печати афиш, флаеров и других материалов.
              </Alert>

              {/* QR для мероприятий */}
              <Paper sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  QR: Все мероприятия
                </Typography>
                <Box sx={{ display: 'inline-block', p: 2, bgcolor: 'white', borderRadius: 2 }}>
                  <QRCodeSVG
                    id="qr-events"
                    value={eventsLink}
                    size={200}
                    level="H"
                    includeMargin={true}
                  />
                </Box>
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<ContentCopyIcon />}
                    onClick={() => handleCopy(eventsLink, 'qr-events')}
                    size="small"
                    sx={{ mr: 1 }}
                  >
                    Копировать ссылку
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => handleDownloadQR('events')}
                    size="small"
                  >
                    Скачать PNG
                  </Button>
                </Box>
              </Paper>

              {/* QR для профиля */}
              <Paper sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  QR: Профиль организатора
                </Typography>
                <Box sx={{ display: 'inline-block', p: 2, bgcolor: 'white', borderRadius: 2 }}>
                  <QRCodeSVG
                    id="qr-profile"
                    value={profileLink}
                    size={200}
                    level="H"
                    includeMargin={true}
                  />
                </Box>
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<ContentCopyIcon />}
                    onClick={() => handleCopy(profileLink, 'qr-profile')}
                    size="small"
                    sx={{ mr: 1 }}
                  >
                    Копировать ссылку
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => handleDownloadQR('profile')}
                    size="small"
                  >
                    Скачать PNG
                  </Button>
                </Box>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Закрыть</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={copiedLink !== null}
        autoHideDuration={2000}
        onClose={() => setCopiedLink(null)}
      >
        <Alert severity="success" onClose={() => setCopiedLink(null)}>
          Скопировано в буфер обмена!
        </Alert>
      </Snackbar>
    </>
  );
};

export default InviteLinks;
